import React, { useEffect, useState } from "react";
import {
  Button,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "react-toastify";
import { creditNoteSchema } from "./schemaCreditNote";
import { useCreateAvoirMutation } from "store/avoir/avoir.api";
import { useGetClientsQuery } from "store/client/clientApi";
import { useGetInvoicesByClientQuery } from "store/invoice/invoiceApi";
import { useNavigate } from "react-router-dom";
import { PATHS } from "config/paths";

const CreateCreditNoteForm = ({ companyId, crediNoteData }) => {
  console.log("🚀 ~ CreateCreditNoteForm ~ companyId:", companyId);
  console.log("🚀 ~ CreateCreditNoteForm ~ crediNoteData:", crediNoteData);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedClientId, setSelectedClientId] = useState(
    crediNoteData?.clientId?._id || ""
  );
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const { data: clientsData } = useGetClientsQuery(companyId);
  console.log("🚀 ~ CreateCreditNoteForm ~ clientsData:", clientsData);
  const { data: invoicesData } = useGetInvoicesByClientQuery(
    { companyId, clientId: selectedClientId },
    { skip: !selectedClientId }
  );
  console.log("🚀 ~ CreateCreditNoteForm ~ invoicesData:", invoicesData);

  const [createAvoir, { isLoading }] = useCreateAvoirMutation();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(creditNoteSchema),
    defaultValues: crediNoteData
      ? crediNoteData
      : {
          invoiceId: crediNoteData ? crediNoteData?.invoiceId?._id : "",
          clientId: crediNoteData ? crediNoteData?.clientId?._id : "",
          note: "",
          creditAmount: 0,
          date: null,
        },
  });

  const selectedInvoiceId = watch("invoiceId");

  useEffect(() => {
    if (selectedInvoiceId) {
      const invoice = invoicesData?.data?.find(
        (inv) => inv._id === selectedInvoiceId
      );
      setSelectedInvoice(invoice);
      if (invoice) {
        setValue("clientId", invoice?.clientId?._id);
        setValue("creditAmount", invoice?.total);
        setValue("invoiceId", invoice._id);
        setValue("date", new Date());
      }
    } else {
      setSelectedInvoice(null);
      setValue("creditAmount", 0);
    }
  }, [selectedInvoiceId, invoicesData, setValue]);

  const onClientChange = (e) => {
    const clientId = e.target.value;
    setSelectedClientId(clientId);
    setValue("invoiceId", "");
    setSelectedInvoice(null);
  };

  const errorHandler = (errors) => {
    Object.values(errors).forEach((error) => {
      if (error?.message) toast.error(t(error.message));
    });
  };

  const submitHandler = async (data) => {
    try {
      await createAvoir({ ...data, companyId }).unwrap();
      toast.success("Avoir créé avec succès !");
      navigate(PATHS.AVOIRS.LIST);
    } catch (err) {
      const message = err?.data?.message || err?.error || "Erreur serveur.";
      toast.error(message);
    }
  };
  // TODO: fix the getting of default values of selected client and invoice when updating credit note
  return (
    <form onSubmit={handleSubmit(submitHandler, errorHandler)}>
      <Stack spacing={3} sx={{ maxWidth: 500 }}>
        {/* Select Client */}
        <Typography variant="h6">{t("avoir.chooseClient")}</Typography>
        <Select
          value={selectedClientId}
          onChange={onClientChange}
          displayEmpty
          fullWidth>
          <MenuItem value="" disabled>
            {t("avoir.chooseClient")}
          </MenuItem>
          {clientsData?.data?.map((client) => (
            <MenuItem key={client._id} value={client._id}>
              {client.name}
            </MenuItem>
          ))}
        </Select>

        <Typography variant="h6">{crediNoteData?.clientId?.name}</Typography>

        {/* Select Invoice */}
        <Typography variant="h6">{t("avoir.chooseInvoice")}</Typography>
        <Select
          {...register("invoiceId", {
            required: t("avoir.invoiceRequired"),
          })}
          displayEmpty
          fullWidth
          value={selectedInvoiceId || ""}
          error={!!errors.invoiceId}>
          <MenuItem value="" disabled>
            {t("avoir.chooseInvoice")}
          </MenuItem>
          {invoicesData?.data?.map((invoice) => (
            <MenuItem key={invoice._id} value={invoice._id}>
              {invoice.numeroInvoice} - {invoice.total.toFixed(2)} €
            </MenuItem>
          ))}
        </Select>

        {errors.invoiceId && (
          <Typography color="error">{errors.invoiceId.message}</Typography>
        )}
        <Typography variant="h6">
          {crediNoteData?.invoiceId?.numeroInvoice}
        </Typography>

        {/* Amount and Note */}
        <Typography variant="body1">
          {t("avoir.invoiceTotal")}:{" "}
          <strong>
            {selectedInvoice ? `${selectedInvoice?.total?.toFixed(2)} €` : "-"}
          </strong>
        </Typography>
        <input type="hidden" {...register("clientId")} />

        <TextField
          label={t("avoir.note")}
          {...register("note")}
          multiline
          rows={3}
          fullWidth
        />

        <TextField
          label={t("avoir.creditAmount")}
          type="number"
          inputProps={{
            min: 0.01,
            step: "any",
          }}
          {...register("creditAmount", {
            required: t("avoir.amountRequired"),
            min: {
              value: 0.01,
              message: t("avoir.positiveAmount"),
            },
            validate: (value) => {
              if (!selectedInvoice) return t("avoir.selectInvoiceFirst");

              return true;
            },
          })}
          error={watch("creditAmount") > (selectedInvoice?.total || 0)}
          helperText={
            watch("creditAmount") > (selectedInvoice?.total || 0)
              ? t("avoir.amountMax", {
                  max: selectedInvoice?.total?.toFixed(2),
                })
              : errors.creditAmount?.message
          }
          fullWidth
        />
        <Button
          variant="contained"
          color="primary"
          type="submit"
          disabled={isLoading}>
          {t("avoir.valid")}
        </Button>
      </Stack>
    </form>
  );
};

export default CreateCreditNoteForm;
